"use client";

import { cn } from "@/lib/utils";
import { useInteractions } from "../hooks/interactionsProvider";
import { buttonVariants } from "@/components/ui/button";
import { EllipsisVertical, Pencil, Trash } from "lucide-react";
import TimestampInput from "@/components/ui/timestamp-input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { interactionTypeOptions } from "../lib/utils";
import { ReactNode } from "react";
import Latex from "@/components/utils/latex";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function InteractionsList() {
    const { dir } = useLocale();
    const {
        interactions,
        getInteractionTitle,
        editInteraction,
        deleteInteraction,
    } = useInteractions();

    return (
        <div
            className={cn(
                "flex w-full flex-col gap-2",
                interactions.length > 0 && "mt-6"
            )}
        >
            {interactions.map((interaction, idx) => (
                <div
                    key={idx}
                    className="flex flex-row items-center gap-4 text-sm"
                >
                    <div className="flex flex-row w-full gap-2 items-center">
                        <span className="text-muted-foreground w-4">
                            {idx + 1}
                        </span>
                        <TimestampInput
                            valueInSeconds={interaction.timestamp}
                            onValueChange={() => {}}
                            triggerClassName="w-[130px]"
                            disabled
                        />
                        <InputLikeDiv className="w-[120px]">
                            <p className="line-clamp-1 w-[120px]">
                                {t(
                                    interactionTypeOptions.find(
                                        (o) => o.id === interaction.type
                                    )?.keyName ?? "",
                                    undefined,
                                    undefined
                                )}
                            </p>
                        </InputLikeDiv>
                        <InputLikeDiv>
                            <p className="line-clamp-1">
                                <Latex
                                    text={getInteractionTitle(interaction.id)}
                                />
                            </p>
                        </InputLikeDiv>
                        <DropdownMenu dir={dir}>
                            <DropdownMenuTrigger>
                                <div
                                    className={cn(
                                        buttonVariants({
                                            variant: "secondary",
                                            size: "icon",
                                        }),
                                        "aspect-square"
                                    )}
                                >
                                    <EllipsisVertical className="size-4" />
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem
                                    onClick={() =>
                                        editInteraction(interaction.id)
                                    }
                                    className="flex flex-row gap-2 items-center"
                                >
                                    <Pencil className="size-4" />
                                    {t(
                                        "video-sheet.interactions-list.options.edit",
                                        undefined,
                                        undefined
                                    )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() =>
                                        deleteInteraction(interaction.id)
                                    }
                                    className="flex flex-row gap-2 items-center"
                                >
                                    <Trash className="size-4" />
                                    {t(
                                        "video-sheet.interactions-list.options.delete",
                                        undefined,
                                        undefined
                                    )}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            ))}
        </div>
    );
}

const InputLikeDiv = ({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) => {
    return (
        <div
            className={cn(
                "flex h-9 cursor-default w-full items-center rounded-md border border-input bg-background px-2.5 py-2 text-sm ring-offset-background",
                className
            )}
        >
            {children}
        </div>
    );
};
